// src/auth/AuthProvider.js
import { createContext, useContext, useEffect, useState } from 'react';

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState('loading'); // 'loading' | 'authed' | 'guest'
  const [sessionRevokedOpen, setSessionRevokedOpen] = useState(false);
  const isLocal = window.location.hostname === "localhost";
  const baseUrl = isLocal
    ? "http://localhost:5000"
    :  window.location.origin;

  // Initial session check
  useEffect(() => {
    fetch(`${baseUrl}/api/users/me`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : Promise.reject()))
      .then(data => {
        setUser(data.user || null);
        setStatus(data.user ? 'authed' : 'guest');
      })
      .catch(() => setStatus('guest'));
  }, []);

  // Listen for "session:revoked" (emitted by fetch interceptor on 401 SESSION_REVOKED)
  useEffect(() => {
    const onRevoked = () => {
      setUser(null);
      setStatus('guest');
      setSessionRevokedOpen(true);
    };

    // If a previous navigation already flagged it
    if (window.localStorage.getItem('session_revoked') === '1') {
      onRevoked();
    }

    window.addEventListener('session:revoked', onRevoked);
    return () => window.removeEventListener('session:revoked', onRevoked);
  }, []);

  const login = async (email, password) => {
    const r = await fetch(`${baseUrl}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include', // send/receive cookie
      body: JSON.stringify({ email, password })
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      throw new Error(err.error || 'Login failed');
    }
    // After login, refresh profile
    const me = await fetch(`${baseUrl}/api/users/me`, { credentials: 'include' }).then(x => x.json());
    setUser(me.user || null);
    setStatus(me.user ? 'authed' : 'guest');
    // Clear any leftover flag
    window.localStorage.removeItem('session_revoked');
    setSessionRevokedOpen(false);
  };

  const logout = async () => {
    await fetch(`${baseUrl}/api/auth/logout`, { method: 'POST', credentials: 'include' });
    setUser(null);
    setStatus('guest');
  };

  const goToLogin = () => {
    window.localStorage.removeItem('session_revoked');
    setSessionRevokedOpen(false);
    window.location.assign('/login?reason=session_revoked');
  };

  const goToReset = () => {
  window.localStorage.removeItem('session_revoked'); // clear the trigger
  setSessionRevokedOpen(false);                      // close modal
  window.location.assign('/reset-password');         // navigate
};


  return (
    <AuthCtx.Provider value={{ user, status, login, logout }}>
      {children}

      {/* Session Revoked Modal */}
      {sessionRevokedOpen && (
        <div
          className="modal-backdrop"
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
          }}
          onClick={goToLogin}
        >
          <div
            className="modal"
            onClick={e => e.stopPropagation()}
            style={{
              width: 'min(520px, 90vw)',
              background: '#fff',
              borderRadius: 12,
              boxShadow: '0 12px 32px rgba(0,0,0,0.25)',
              padding: 24
            }}
          >
            <div className="brand" style={{ fontSize: 18, marginBottom: 8 }}>Auto<b>nect</b></div>
            <h3 style={{ margin: '4px 0 12px', fontSize: 20 }}>
              Another Device Logged In to Your Account
            </h3>
            <p style={{ margin: '0 0 16px', lineHeight: 1.5 }}>
              You were signed out because your account was used on another device.
              <br />
              <b>Log back in</b> to access the application. If this wasn’t you, <b>change your password immediately</b>.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={goToLogin}>Go to Login</button>
                <button
                  className="btn btn-primary"
                  onClick={goToReset}   // <-- use this instead of window.location.assign(...)
                >
                  Change Password
                </button>
            </div>
          </div>
        </div>
      )}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  return useContext(AuthCtx);
}
